import Tool, { type ToolButtonComponent } from './Tool';
import React, { useRef, useState, useEffect } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { isMindMap } from '../objects/guards';
import type MindMap from '../objects/MindMap';
import {
  ConnectorRouting,
  type TConnectorRouting,
} from '../objects/ConnectorRouting';
import lineSVG from '../assets/icons/line.svg';
import lineOrthogonalSVG from '../assets/icons/lineOrthogonal.svg';
import { svgToIcon } from '../utils/svg';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import classNames from 'classnames';
import styles from '../styles.module.css';
import { type Instance as TippyInstance } from 'tippy.js/index';

function iconForRouting(routing: TConnectorRouting) {
  let iconFile: string;
  switch (routing) {
    case ConnectorRouting.ROUTING_STRAIGHT:
      iconFile = lineSVG;
      break;
    case ConnectorRouting.ROUTING_ORTHOGONAL_AUTO:
    case ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL:
      iconFile = lineOrthogonalSVG;
      break;
    default:
      iconFile = lineSVG;
  }

  return svgToIcon(iconFile);
}

const Popup: React.FC<{
  mindmap: MindMap;
  currentRouting: TConnectorRouting;
  setCurrentRouting: (routing: TConnectorRouting) => void;
}> = ApplicationView(({ mindmap, currentRouting, setCurrentRouting }) => {
  const controller = useController(WhiteboardController);

  const setLineRouting = (routing: TConnectorRouting) => {
    setCurrentRouting(routing);
    mindmap.setLineRouting(routing);
    Tool.toolActionComplete({ controller });
  };

  return (
    <ToolbarMenu columns={2}>
      <ToolButton
        className={classNames({
          [styles.toolButtonActive]:
            currentRouting === ConnectorRouting.ROUTING_STRAIGHT,
        })}
        onClick={() => setLineRouting(ConnectorRouting.ROUTING_STRAIGHT)}
      >
        {iconForRouting(ConnectorRouting.ROUTING_STRAIGHT)}
      </ToolButton>
      <ToolButton
        className={classNames({
          [styles.toolButtonActive]:
            currentRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO ||
            currentRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL,
        })}
        onClick={() => setLineRouting(ConnectorRouting.ROUTING_ORTHOGONAL_AUTO)}
      >
        {iconForRouting(ConnectorRouting.ROUTING_ORTHOGONAL_AUTO)}
      </ToolButton>
    </ToolbarMenu>
  );
});

const ConnectorStyleButton: ToolButtonComponent = ApplicationView(
  ({ tool, controller }) => {
    const { selection: mindmap } = controller;
    if (!isMindMap(mindmap)) return null;

    const tippyRef = useRef<TippyInstance>();
    const lineRouting = mindmap.lineRouting;
    const [currentRouting, setCurrentRouting] = useState(lineRouting);

    // Update routing when the mindmap's lineRouting changes
    useEffect(() => {
      setCurrentRouting(lineRouting);
    }, [lineRouting]);

    return (
      <ToolButton
        popup={
          <Popup
            mindmap={mindmap}
            currentRouting={currentRouting}
            setCurrentRouting={setCurrentRouting}
          />
        }
        tippyRef={tippyRef}
        id='mindmapConnectorStyle'
        tool={tool}
      >
        {iconForRouting(currentRouting)}
      </ToolButton>
    );
  }
);

class ToolMindMapConnectorStyle extends Tool {
  static toolName = 'Connector style';

  static action() {}

  static enabled() {
    return true;
  }

  static buttonComponent() {
    return ConnectorStyleButton;
  }
}

export default ToolMindMapConnectorStyle;
